import { useSearchParams } from 'react-router-dom';
import {
  DatabaseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  AutoComplete,
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import type { UploadFile } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ProCard,
  ProFormSelect,
  ProFormText,
  ProTable,
  QueryFilter,
  type ActionType,
  type ProColumns,
  type ProFormInstance,
} from '@ant-design/pro-components';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTenantDateTime } from '../lib/tenant';

type OriginType = 'COMMERCIAL' | 'IN_HOUSE' | 'DERIVED' | 'OTHER';
type CellCategory =
  | 'NORMAL'
  | 'TUMOR'
  | 'IMMORTALIZED'
  | 'PRIMARY'
  | 'STEM_CELL'
  | 'OTHER';
type GrowthMode = 'ADHERENT' | 'SUSPENSION' | 'SEMI_ADHERENT' | 'OTHER';
type BiosafetyLevel = 'BSL_1' | 'BSL_2' | 'BSL_3' | 'OTHER';

interface CellLine {
  id: string;
  catalogId?: string | null;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  species: string;
  originType: OriginType;
  tissueOrigin?: string | null;
  cellCategory?: CellCategory | null;
  disease?: string | null;
  growthMode?: GrowthMode | null;
  cultureMedium?: string | null;
  serum?: string | null;
  antibiotics?: string | null;
  cultureConditions?: string | null;
  passageRatio?: string | null;
  doublingTime?: string | null;
  biosafetyLevel?: BiosafetyLevel | null;
  status: 'ACTIVE' | 'INACTIVE';
  version: number;
  source?: string | null;
  catalogNo?: string | null;
  description?: string | null;
  createdAt: string;
}

interface CellLineCatalogEntry {
  id: string;
  code: string;
  canonicalAccession?: string | null;
  nameZh?: string | null;
  nameEn?: string | null;
  species: string;
  originType: OriginType;
  tissueOrigin?: string | null;
  cellCategory?: string | null;
  disease?: string | null;
  growthMode?: string | null;
  cultureMedium?: string | null;
  sourceName: string;
  sourceRecordId?: string | null;
  riskStatus: 'CLEAR' | 'REVIEW' | 'MISIDENTIFIED' | 'CONTAMINATION_REPORTED';
  riskNote?: string | null;
  curationStatus: 'DRAFT' | 'APPROVED' | 'RETIRED';
  enabled: boolean;
  cellLineId?: string | null;
}

interface CellLineForm {
  code: string;
  species: string;
  originType: OriginType;
  tissueOrigin?: string | undefined;
  cellCategory?: CellCategory | undefined;
  disease?: string | undefined;
  growthMode?: GrowthMode | undefined;
  cultureMedium?: string | undefined;
  serum?: string | undefined;
  antibiotics?: string | undefined;
  cultureConditions?: string | undefined;
  passageRatio?: string | undefined;
  doublingTime?: string | undefined;
  biosafetyLevel?: BiosafetyLevel | undefined;
  source?: string | undefined;
  catalogNo?: string | undefined;
  description?: string | undefined;
  status?: 'ACTIVE' | 'INACTIVE' | undefined;
}

interface CellLineQueryForm {
  query?: string | undefined;
  species?: string | undefined;
  originType?: OriginType | undefined;
  status?: 'ACTIVE' | 'INACTIVE' | undefined;
}

interface ImportJob {
  id: string;
  importNo?: string;
  status: 'PRECHECKED' | 'EXECUTING' | 'COMPLETED' | 'FAILED';
  objectType: 'SAMPLE' | 'CELL_LINE';
  sourceFormat: 'XLSX' | 'CSV';
  originalFilename?: string;
  totalRows: number;
  validRows: number;
  errorRows: number;
  version: number;
  executedAt?: string | null;
  errors: {
    rowNumber: number;
    field?: string | null;
    code: string;
    messageKey: string;
  }[];
}

const CELL_LINE_TABLE_COLUMN_KEYS = [
  'code',
  'species',
  'originType',
  'cellCategory',
  'growthMode',
  'tissueOrigin',
  'disease',
  'cultureMedium',
  'serum',
  'antibiotics',
  'cultureConditions',
  'passageRatio',
  'doublingTime',
  'biosafetyLevel',
  'source',
  'catalogNo',
  'description',
  'status',
  'createdAt',
] as const;
const DEFAULT_VISIBLE_COLUMN_KEYS = [
  'code',
  'species',
  'originType',
  'cellCategory',
  'growthMode',
  'cultureMedium',
  'status',
  'createdAt',
] as const;

type CellLineColumnKey = (typeof CELL_LINE_TABLE_COLUMN_KEYS)[number];

export function CellLinesPage() {
  const [urlParams, setUrlParams] = useSearchParams();
  const operatorId = urlParams.get('operatorId') ?? '';

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogEntries, setCatalogEntries] = useState<CellLineCatalogEntry[]>(
    [],
  );
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogSpecies, setCatalogSpecies] = useState<string>();
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<string[]>([]);
  const [editing, setEditing] = useState<CellLine>();
  const [activeFilters, setActiveFilters] = useState<CellLineQueryForm>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [file, setFile] = useState<UploadFile>();
  const [importJob, setImportJob] = useState<ImportJob>();
  const [form] = Form.useForm<CellLineForm>();
  const [messageApi, contextHolder] = message.useMessage();
  const actionRef = useRef<ActionType | undefined>(undefined);
  const queryFormRef = useRef<ProFormInstance | undefined>(undefined);
  const originTypeOptions = (
    ['COMMERCIAL', 'IN_HOUSE', 'DERIVED', 'OTHER'] as OriginType[]
  ).map((value) => ({
    value,
    label: t(`cellLines.originTypeOptions.${value}`),
  }));
  const statusOptions = (['ACTIVE', 'INACTIVE'] as const).map((value) => ({
    value,
    label: t(`cellLines.statusOptions.${value}`),
  }));
  const cellCategoryOptions = (
    [
      'NORMAL',
      'TUMOR',
      'IMMORTALIZED',
      'PRIMARY',
      'STEM_CELL',
      'OTHER',
    ] as CellCategory[]
  ).map((value) => ({
    value,
    label: t(`cellLines.cellCategoryOptions.${value}`),
  }));
  const growthModeOptions = (
    ['ADHERENT', 'SUSPENSION', 'SEMI_ADHERENT', 'OTHER'] as GrowthMode[]
  ).map((value) => ({
    value,
    label: t(`cellLines.growthModeOptions.${value}`),
  }));
  const biosafetyLevelOptions = (
    ['BSL_1', 'BSL_2', 'BSL_3', 'OTHER'] as BiosafetyLevel[]
  ).map((value) => ({
    value,
    label: t(`cellLines.biosafetyLevelOptions.${value}`),
  }));
  const speciesOptions = [
    'Homo sapiens',
    'Mus musculus',
    'Rattus norvegicus',
    'Cricetulus griseus',
    'Chlorocebus sabaeus',
    'Mesocricetus auratus',
  ].map((value) => ({ value }));

  async function requestCellLines(requestParams: Record<string, unknown>) {
    setLoading(true);
    try {
      const searchParams = new URLSearchParams({ limit: '100' });
      if (operatorId) searchParams.set('operatorId', operatorId);
      const queryValue = requestParams.query;
      const speciesValue = requestParams.species;
      const originTypeValue = requestParams.originType;
      const statusValue = requestParams.status;
      if (typeof queryValue === 'string' && queryValue.trim())
        searchParams.set('query', queryValue.trim());
      if (typeof speciesValue === 'string' && speciesValue)
        searchParams.set('species', speciesValue);
      if (typeof originTypeValue === 'string' && originTypeValue)
        searchParams.set('originType', originTypeValue);
      if (typeof statusValue === 'string' && statusValue)
        searchParams.set('status', statusValue);
      const response = await fetch(
        `/api/v1/cell-lines?${searchParams.toString()}`,
        {
          credentials: 'include',
        },
      );
      if (response.status === 401) {
        window.location.assign('/login');
        return { data: [], success: false, total: 0 };
      }
      if (!response.ok) throw new Error(t('cellLines.loadFailed'));
      const body = (await response.json()) as { items: CellLine[] };
      return { data: body.items, success: true, total: body.items.length };
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('cellLines.loadFailed'),
      );
      return { data: [], success: false, total: 0 };
    } finally {
      setLoading(false);
    }
  }

  async function loadCatalog(query = catalogQuery, species = catalogSpecies) {
    setCatalogLoading(true);
    try {
      const searchParams = new URLSearchParams({ limit: '200' });
      if (query.trim()) searchParams.set('query', query.trim());
      if (species) searchParams.set('species', species);
      const response = await fetch(
        `/api/v1/cell-lines/catalog?${searchParams.toString()}`,
        { credentials: 'include' },
      );
      if (response.status === 401) {
        window.location.assign('/login');
        return;
      }
      if (!response.ok) throw new Error(t('cellLines.catalogLoadFailed'));
      const body = (await response.json()) as {
        items: CellLineCatalogEntry[];
      };
      setCatalogEntries(body.items);
      setSelectedCatalogIds((current) =>
        current.filter((id) => body.items.some((item) => item.id === id)),
      );
    } catch (error) {
      await messageApi.error(
        error instanceof Error
          ? error.message
          : t('cellLines.catalogLoadFailed'),
      );
    } finally {
      setCatalogLoading(false);
    }
  }

  async function enableSelectedCatalog() {
    if (!selectedCatalogIds.length) return;
    setCatalogLoading(true);
    try {
      const response = await fetch('/api/v1/cell-lines/catalog/enable', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ catalogIds: selectedCatalogIds }),
      });
      if (!response.ok) throw new Error(t('cellLines.catalogEnableFailed'));
      const body = (await response.json()) as {
        createdCount: number;
        skipped: Array<{ catalogId: string; reason: string }>;
      };
      setSelectedCatalogIds([]);
      await actionRef.current?.reload();
      await loadCatalog();
      if (body.skipped.length > 0) {
        await messageApi.warning(
          t('cellLines.catalogEnablePartial', {
            created: body.createdCount,
            skipped: body.skipped.length,
          }),
        );
      } else {
        await messageApi.success(
          t('cellLines.catalogEnabled', { count: body.createdCount }),
        );
      }
    } catch (error) {
      await messageApi.error(
        error instanceof Error
          ? error.message
          : t('cellLines.catalogEnableFailed'),
      );
    } finally {
      setCatalogLoading(false);
    }
  }

  async function save(values: CellLineForm) {
    const response = await fetch(
      `/api/v1/cell-lines${editing ? `/${editing.id}` : ''}`,
      {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          editing
            ? {
                ...values,
                version: editing.version,
                status: values.status ?? editing.status,
              }
            : values,
        ),
      },
    );
    if (!response.ok) {
      let errorMessage = t('cellLines.saveFailed');
      if (response.status === 409) {
        const body = (await response.json().catch(() => null)) as {
          message?: unknown;
        } | null;
        errorMessage =
          body?.message === 'Cell line name or alias already exists'
            ? t('cellLines.duplicateName')
            : body?.message ===
                'Cell line matches a standard catalog name or alias; enable it from the standard library'
              ? t('cellLines.duplicateCatalog')
              : t('cellLines.duplicate');
      }
      await messageApi.error(errorMessage);
      return;
    }
    setOpen(false);
    setEditing(undefined);
    form.resetFields();
    await actionRef.current?.reload();
    await messageApi.success(t('cellLines.saved'));
  }

  async function deleteCellLine(row: CellLine) {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/cell-lines/${row.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ version: row.version }),
      });
      if (!response.ok)
        throw new Error(
          response.status === 409
            ? t('cellLines.deleteBlocked')
            : t('cellLines.deleteFailed'),
        );
      setSelectedIds((current) => current.filter((id) => id !== row.id));
      await actionRef.current?.reload();
      await messageApi.success(t('cellLines.deleted'));
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('cellLines.deleteFailed'),
      );
    } finally {
      setLoading(false);
    }
  }

  async function deleteSelectedCellLines() {
    if (!selectedIds.length) return;
    setLoading(true);
    try {
      const response = await fetch('/api/v1/cell-lines/bulk-delete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (!response.ok) throw new Error(t('cellLines.deleteFailed'));
      const body = (await response.json()) as {
        deletedCount: number;
        blockedCount: number;
      };
      setSelectedIds([]);
      await actionRef.current?.reload();
      if (body.blockedCount > 0) {
        await messageApi.warning(
          t('cellLines.bulkDeletePartial', {
            deleted: body.deletedCount,
            blocked: body.blockedCount,
          }),
        );
        return;
      }
      await messageApi.success(
        t('cellLines.bulkDeleted', { count: body.deletedCount }),
      );
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('cellLines.deleteFailed'),
      );
    } finally {
      setLoading(false);
    }
  }

  async function precheckImport() {
    if (!file?.originFileObj) return;
    const body = new FormData();
    body.append('file', file.originFileObj);
    const response = await fetch('/api/v1/imports/cell-line/precheck', {
      method: 'POST',
      credentials: 'include',
      headers: { 'idempotency-key': crypto.randomUUID() },
      body,
    });
    if (!response.ok) {
      await messageApi.error(t('cellLines.importFailed'));
      return;
    }
    setImportJob((await response.json()) as ImportJob);
  }

  async function executeImport() {
    if (!importJob) return;
    const response = await fetch(`/api/v1/imports/${importJob.id}/execute`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version: importJob.version }),
    });
    if (!response.ok) {
      await messageApi.error(t('cellLines.importFailed'));
      return;
    }
    setImportJob((await response.json()) as ImportJob);
    setFile(undefined);
    await actionRef.current?.reload();
    await messageApi.success(t('cellLines.imported'));
  }

  function closeImportModal() {
    setImportOpen(false);
    setFile(undefined);
    setImportJob(undefined);
  }

  function editCellLine(row: CellLine) {
    form.setFieldsValue({
      code: row.code,
      species: row.species,
      originType: row.originType,
      tissueOrigin: row.tissueOrigin ?? undefined,
      disease: row.disease ?? undefined,
      cellCategory: row.cellCategory ?? undefined,
      growthMode: row.growthMode ?? undefined,
      cultureMedium: row.cultureMedium ?? undefined,
      serum: row.serum ?? undefined,
      antibiotics: row.antibiotics ?? undefined,
      cultureConditions: row.cultureConditions ?? undefined,
      passageRatio: row.passageRatio ?? undefined,
      doublingTime: row.doublingTime ?? undefined,
      biosafetyLevel: row.biosafetyLevel ?? undefined,
      source: row.source ?? undefined,
      catalogNo: row.catalogNo ?? undefined,
      description: row.description ?? undefined,
      status: row.status,
    });
    setEditing(row);
    setOpen(true);
  }

  function displayText(value: string | null | undefined) {
    return value?.trim() || '-';
  }

  const columnLabels = useMemo<Record<CellLineColumnKey, string>>(
    () => ({
      code: t('cellLines.code'),
      species: t('cellLines.species'),
      originType: t('cellLines.originType'),
      cellCategory: t('cellLines.cellCategory'),
      growthMode: t('cellLines.growthMode'),
      tissueOrigin: t('cellLines.tissueOrigin'),
      disease: t('cellLines.disease'),
      cultureMedium: t('cellLines.cultureMedium'),
      serum: t('cellLines.serum'),
      antibiotics: t('cellLines.antibiotics'),
      cultureConditions: t('cellLines.cultureConditions'),
      passageRatio: t('cellLines.passageRatio'),
      doublingTime: t('cellLines.doublingTime'),
      biosafetyLevel: t('cellLines.biosafetyLevel'),
      source: t('cellLines.source'),
      catalogNo: t('cellLines.catalogNo'),
      description: t('cellLines.description'),
      status: t('cellLines.status'),
      createdAt: t('cellLines.createdAt'),
    }),
    [t],
  );

  const columnDefinitions = useMemo<
    Record<CellLineColumnKey, ProColumns<CellLine>>
  >(
    () => ({
      code: {
        title: columnLabels.code,
        dataIndex: 'code',
        key: 'code',
        width: 140,
      },
      species: {
        title: columnLabels.species,
        dataIndex: 'species',
        key: 'species',
        width: 150,
        ellipsis: true,
      },
      originType: {
        title: columnLabels.originType,
        key: 'originType',
        width: 130,
        ellipsis: true,
        render: (_, row) => t(`cellLines.originTypeOptions.${row.originType}`),
      },
      cellCategory: {
        title: columnLabels.cellCategory,
        key: 'cellCategory',
        width: 130,
        ellipsis: true,
        render: (_, row) =>
          row.cellCategory
            ? t(`cellLines.cellCategoryOptions.${row.cellCategory}`)
            : '-',
      },
      growthMode: {
        title: columnLabels.growthMode,
        key: 'growthMode',
        width: 130,
        ellipsis: true,
        render: (_, row) =>
          row.growthMode
            ? t(`cellLines.growthModeOptions.${row.growthMode}`)
            : '-',
      },
      tissueOrigin: {
        title: columnLabels.tissueOrigin,
        dataIndex: 'tissueOrigin',
        key: 'tissueOrigin',
        width: 140,
        ellipsis: true,
        render: (_, row) => displayText(row.tissueOrigin),
      },
      disease: {
        title: columnLabels.disease,
        dataIndex: 'disease',
        key: 'disease',
        width: 160,
        ellipsis: true,
        render: (_, row) => displayText(row.disease),
      },
      cultureMedium: {
        title: columnLabels.cultureMedium,
        dataIndex: 'cultureMedium',
        key: 'cultureMedium',
        width: 160,
        ellipsis: true,
        render: (_, row) => displayText(row.cultureMedium),
      },
      serum: {
        title: columnLabels.serum,
        dataIndex: 'serum',
        key: 'serum',
        width: 120,
        ellipsis: true,
        render: (_, row) => displayText(row.serum),
      },
      antibiotics: {
        title: columnLabels.antibiotics,
        dataIndex: 'antibiotics',
        key: 'antibiotics',
        width: 140,
        ellipsis: true,
        render: (_, row) => displayText(row.antibiotics),
      },
      cultureConditions: {
        title: columnLabels.cultureConditions,
        dataIndex: 'cultureConditions',
        key: 'cultureConditions',
        width: 180,
        ellipsis: true,
        render: (_, row) => displayText(row.cultureConditions),
      },
      passageRatio: {
        title: columnLabels.passageRatio,
        dataIndex: 'passageRatio',
        key: 'passageRatio',
        width: 120,
        ellipsis: true,
        render: (_, row) => displayText(row.passageRatio),
      },
      doublingTime: {
        title: columnLabels.doublingTime,
        dataIndex: 'doublingTime',
        key: 'doublingTime',
        width: 120,
        ellipsis: true,
        render: (_, row) => displayText(row.doublingTime),
      },
      biosafetyLevel: {
        title: columnLabels.biosafetyLevel,
        key: 'biosafetyLevel',
        width: 130,
        ellipsis: true,
        render: (_, row) =>
          row.biosafetyLevel
            ? t(`cellLines.biosafetyLevelOptions.${row.biosafetyLevel}`)
            : '-',
      },
      source: {
        title: columnLabels.source,
        dataIndex: 'source',
        key: 'source',
        width: 140,
        ellipsis: true,
        render: (_, row) => displayText(row.source),
      },
      catalogNo: {
        title: columnLabels.catalogNo,
        dataIndex: 'catalogNo',
        key: 'catalogNo',
        width: 130,
        ellipsis: true,
        render: (_, row) => displayText(row.catalogNo),
      },
      description: {
        title: columnLabels.description,
        dataIndex: 'description',
        key: 'description',
        width: 180,
        ellipsis: true,
        render: (_, row) => displayText(row.description),
      },
      status: {
        title: columnLabels.status,
        key: 'status',
        width: 100,
        render: (_, row) => <Tag color="green">{row.status}</Tag>,
      },
      createdAt: {
        title: columnLabels.createdAt,
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        render: (_, row) => formatTenantDateTime(row.createdAt),
      },
    }),
    [columnLabels, t],
  );

  const columns = useMemo<ProColumns<CellLine>[]>(
    () => [
      ...CELL_LINE_TABLE_COLUMN_KEYS.map((key) => columnDefinitions[key]),
      {
        title: t('cellLines.actions'),
        key: 'actions',
        fixed: 'right',
        width: 170,
        render: (_, row) => (
          <Space size="small" wrap={false}>
            <Button type="link" size="small" onClick={() => editCellLine(row)}>
              {t('cellLines.edit')}
            </Button>
            <Popconfirm
              title={t('cellLines.deleteConfirm')}
              okText={t('cellLines.delete')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true }}
              onConfirm={() => void deleteCellLine(row)}
            >
              <Button type="link" danger size="small">
                {t('cellLines.delete')}
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [columnDefinitions, t],
  );

  const catalogColumns = useMemo<ColumnsType<CellLineCatalogEntry>>(
    () => [
      {
        title: t('cellLines.code'),
        dataIndex: 'code',
        width: 130,
        fixed: 'left',
      },
      {
        title: t('cellLines.name'),
        width: 180,
        render: (_, row) => row.nameZh || row.nameEn || '-',
      },
      {
        title: t('cellLines.species'),
        dataIndex: 'species',
        width: 150,
      },
      {
        title: t('cellLines.cellCategory'),
        width: 120,
        render: (_, row) =>
          row.cellCategory
            ? t(`cellLines.cellCategoryOptions.${row.cellCategory}`)
            : '-',
      },
      {
        title: t('cellLines.growthMode'),
        width: 110,
        render: (_, row) =>
          row.growthMode
            ? t(`cellLines.growthModeOptions.${row.growthMode}`)
            : '-',
      },
      {
        title: t('cellLines.catalogRisk'),
        width: 150,
        render: (_, row) => (
          <Tag color={row.riskStatus === 'CLEAR' ? 'green' : 'orange'}>
            {t(`cellLines.catalogRiskOptions.${row.riskStatus}`)}
          </Tag>
        ),
      },
      {
        title: t('cellLines.catalogStatus'),
        width: 110,
        render: (_, row) => (
          <Tag color={row.enabled ? 'blue' : 'default'}>
            {row.enabled
              ? t('cellLines.catalogEnabledStatus')
              : t('cellLines.catalogAvailableStatus')}
          </Tag>
        ),
      },
    ],
    [t],
  );

  return (
    <section className="cell-lines-page">
      {contextHolder}
      <ProCard className="cell-lines-query-card" variant="outlined">
        <QueryFilter
          formRef={queryFormRef}
          defaultCollapsed={false}
          span={6}
          searchGutter={12}
          labelWidth="auto"
          initialValues={activeFilters}
          onFinish={(values: CellLineQueryForm) => {
            setActiveFilters({
              query: values.query?.trim() || undefined,
              species: values.species,
              originType: values.originType,
              status: values.status,
            });
          }}
          onReset={() => {
            queryFormRef.current?.resetFields();
            setActiveFilters({});
          }}
        >
          <ProFormText
            name="query"
            label={t('cellLines.search')}
            width="lg"
            fieldProps={{
              allowClear: true,
              placeholder: t('cellLines.searchPlaceholder'),
            }}
          />
          <ProFormSelect
            name="species"
            label={t('cellLines.species')}
            width="sm"
            options={speciesOptions}
            fieldProps={{ showSearch: true, optionFilterProp: 'label' }}
          />
          <ProFormSelect
            name="originType"
            label={t('cellLines.originType')}
            width="sm"
            options={originTypeOptions}
          />
          <ProFormSelect
            name="status"
            label={t('cellLines.status')}
            width="sm"
            options={statusOptions}
          />
        </QueryFilter>
      </ProCard>
      {operatorId && (
        <Tag
          closable
          onClose={() => {
            const next = new URLSearchParams(urlParams);
            next.delete('operatorId');
            setUrlParams(next);
          }}
        >
          {t('workplace.stats.myCellLines')}
        </Tag>
      )}
      <ProTable<CellLine>
        className="cell-lines-table-card"
        rowKey="id"
        actionRef={actionRef}
        columns={columns}
        params={{ ...activeFilters, operatorId }}
        request={requestCellLines}
        onLoad={(data) => {
          setSelectedIds((current) =>
            current.filter((id) => data.some((item) => item.id === id)),
          );
        }}
        headerTitle={t('nav.cellLines')}
        search={false}
        cardBordered
        options={{
          reload: true,
          density: true,
          setting: true,
          fullScreen: true,
        }}
        columnsState={{
          persistenceKey: 'cell-line-pro-table-columns',
          persistenceType: 'localStorage',
          defaultValue: Object.fromEntries(
            CELL_LINE_TABLE_COLUMN_KEYS.map((key, index) => [
              key,
              {
                show: DEFAULT_VISIBLE_COLUMN_KEYS.includes(
                  key as (typeof DEFAULT_VISIBLE_COLUMN_KEYS)[number],
                ),
                order: index,
              },
            ]),
          ),
        }}
        toolBarRender={() => [
          <Button
            key="catalog"
            icon={<DatabaseOutlined />}
            onClick={() => {
              setCatalogOpen(true);
              setSelectedCatalogIds([]);
              void loadCatalog();
            }}
          >
            {t('cellLines.catalog')}
          </Button>,
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({ originType: 'COMMERCIAL' });
              setEditing(undefined);
              setOpen(true);
            }}
          >
            {t('cellLines.create')}
          </Button>,
          <Button
            key="import"
            icon={<UploadOutlined />}
            onClick={() => setImportOpen(true)}
          >
            {t('cellLines.import')}
          </Button>,
        ]}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys.map(String)),
        }}
        tableAlertOptionRender={(props) =>
          selectedIds.length > 0 ? (
            <Space size="small">
              <Popconfirm
                title={t('cellLines.deleteSelectedConfirm', {
                  count: selectedIds.length,
                })}
                okText={t('cellLines.deleteSelected')}
                cancelText={t('common.cancel')}
                okButtonProps={{ danger: true }}
                onConfirm={() => void deleteSelectedCellLines()}
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  {t('cellLines.deleteSelected')}
                </Button>
              </Popconfirm>
              <Button type="link" onClick={props.onCleanSelected}>
                {t('common.clearSelection')}
              </Button>
            </Space>
          ) : null
        }
        pagination={{ defaultPageSize: 50 }}
      />
      <Modal
        title={t('cellLines.catalogTitle')}
        open={catalogOpen}
        width={1120}
        onCancel={() => {
          setCatalogOpen(false);
          setSelectedCatalogIds([]);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setCatalogOpen(false);
              setSelectedCatalogIds([]);
            }}
          >
            {t('common.close')}
          </Button>,
          <Button
            key="enable"
            type="primary"
            disabled={!selectedCatalogIds.length}
            loading={catalogLoading}
            onClick={() => void enableSelectedCatalog()}
          >
            {t('cellLines.catalogEnableSelected')}
          </Button>,
        ]}
      >
        <div className="cell-line-catalog-toolbar">
          <Input.Search
            allowClear
            value={catalogQuery}
            placeholder={t('cellLines.catalogSearchPlaceholder')}
            onChange={(event) => setCatalogQuery(event.target.value)}
            onSearch={(value) => void loadCatalog(value, catalogSpecies)}
          />
          <Select
            allowClear
            showSearch
            value={catalogSpecies}
            options={speciesOptions}
            placeholder={t('cellLines.species')}
            onChange={(value) => {
              setCatalogSpecies(value);
              void loadCatalog(catalogQuery, value);
            }}
          />
        </div>
        <Table<CellLineCatalogEntry>
          rowKey="id"
          loading={catalogLoading}
          columns={catalogColumns}
          dataSource={catalogEntries}
          pagination={false}
          scroll={{ x: 980, y: 440 }}
          rowSelection={{
            selectedRowKeys: selectedCatalogIds,
            onChange: (keys) => setSelectedCatalogIds(keys.map(String)),
            getCheckboxProps: (row) => ({ disabled: row.enabled }),
          }}
          locale={{ emptyText: t('cellLines.catalogEmpty') }}
        />
      </Modal>
      <Modal
        title={editing ? t('cellLines.editTitle') : t('cellLines.createTitle')}
        open={open}
        width={860}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onCancel={() => setOpen(false)}
        onOk={() => void form.submit()}
        confirmLoading={loading}
      >
        <Form<CellLineForm>
          form={form}
          layout="vertical"
          onFinish={(values) => void save(values)}
        >
          <div className="cell-line-form-grid">
            <Form.Item name="code" label={t('cellLines.code')}>
              <Input
                disabled={!!editing}
                placeholder={t('cellLines.codePlaceholder')}
              />
            </Form.Item>
            <Form.Item
              name="species"
              label={t('cellLines.species')}
              rules={[{ required: true }]}
            >
              <AutoComplete
                options={speciesOptions}
                placeholder={t('cellLines.speciesPlaceholder')}
                filterOption={(inputValue, option) =>
                  String(option?.value ?? '')
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item
              name="originType"
              label={t('cellLines.originType')}
              initialValue="COMMERCIAL"
              rules={[{ required: true }]}
            >
              <Select options={originTypeOptions} />
            </Form.Item>
          </div>
          <Divider orientation="horizontal" titlePlacement="left">
            {t('cellLines.optionalInfo')}
          </Divider>
          <div className="cell-line-form-grid">
            <Form.Item name="cellCategory" label={t('cellLines.cellCategory')}>
              <Select allowClear options={cellCategoryOptions} />
            </Form.Item>
            <Form.Item name="growthMode" label={t('cellLines.growthMode')}>
              <Select allowClear options={growthModeOptions} />
            </Form.Item>
            <Form.Item name="tissueOrigin" label={t('cellLines.tissueOrigin')}>
              <Input />
            </Form.Item>
            <Form.Item name="disease" label={t('cellLines.disease')}>
              <Input />
            </Form.Item>
            <Form.Item
              name="cultureMedium"
              label={t('cellLines.cultureMedium')}
            >
              <Input placeholder={t('cellLines.cultureMediumPlaceholder')} />
            </Form.Item>
            <Form.Item name="serum" label={t('cellLines.serum')}>
              <Input placeholder={t('cellLines.serumPlaceholder')} />
            </Form.Item>
            <Form.Item name="antibiotics" label={t('cellLines.antibiotics')}>
              <Input placeholder={t('cellLines.antibioticsPlaceholder')} />
            </Form.Item>
            <Form.Item
              name="cultureConditions"
              label={t('cellLines.cultureConditions')}
            >
              <Input
                placeholder={t('cellLines.cultureConditionsPlaceholder')}
              />
            </Form.Item>
            <Form.Item name="passageRatio" label={t('cellLines.passageRatio')}>
              <Input placeholder={t('cellLines.passageRatioPlaceholder')} />
            </Form.Item>
            <Form.Item name="doublingTime" label={t('cellLines.doublingTime')}>
              <Input placeholder={t('cellLines.doublingTimePlaceholder')} />
            </Form.Item>
            <Form.Item
              name="biosafetyLevel"
              label={t('cellLines.biosafetyLevel')}
            >
              <Select allowClear options={biosafetyLevelOptions} />
            </Form.Item>
            <Form.Item name="source" label={t('cellLines.source')}>
              <Input />
            </Form.Item>
            <Form.Item name="catalogNo" label={t('cellLines.catalogNo')}>
              <Input />
            </Form.Item>
            {editing && (
              <Form.Item name="status" label={t('cellLines.status')}>
                <Select
                  options={['ACTIVE', 'INACTIVE'].map((value) => ({
                    value,
                    label: value,
                  }))}
                />
              </Form.Item>
            )}
          </div>
          <Form.Item name="description" label={t('cellLines.description')}>
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={t('cellLines.importTitle')}
        open={importOpen}
        onCancel={closeImportModal}
        width={760}
        footer={
          <Space>
            <Button onClick={closeImportModal}>
              {importJob?.status === 'COMPLETED'
                ? t('common.close')
                : t('common.cancel')}
            </Button>
            {importJob && importJob.status !== 'COMPLETED' ? (
              <Button type="primary" onClick={() => void executeImport()}>
                {t('cellLines.executeImport')}
              </Button>
            ) : null}
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Button
            icon={<DownloadOutlined />}
            href="/api/v1/imports/cell-line/template"
          >
            {t('cellLines.template')}
          </Button>
          <Upload
            accept=".xlsx,.csv"
            fileList={file ? [file] : []}
            maxCount={1}
            beforeUpload={(next) => {
              setFile(next);
              setImportJob(undefined);
              return false;
            }}
            onRemove={() => {
              setFile(undefined);
              setImportJob(undefined);
              return true;
            }}
          >
            <Button icon={<UploadOutlined />}>
              {t('cellLines.selectFile')}
            </Button>
          </Upload>
          <Button
            type="primary"
            disabled={!file}
            onClick={() => void precheckImport()}
          >
            {t('cellLines.precheck')}
          </Button>
        </Space>
        {importJob && (
          <Alert
            showIcon
            type={
              importJob.status === 'COMPLETED' && importJob.errorRows === 0
                ? 'success'
                : importJob.errorRows
                  ? 'warning'
                  : 'info'
            }
            message={
              importJob.status === 'COMPLETED'
                ? t('cellLines.importCompletedSummary')
                : t('cellLines.importPrecheckSummary')
            }
            style={{ marginBottom: 12 }}
          />
        )}
        {importJob && (
          <p>
            {importJob.importNo ? <Tag>{importJob.importNo}</Tag> : null}
            {t('cellLines.importStatus')}:{' '}
            <Tag color={importJob.status === 'COMPLETED' ? 'green' : 'blue'}>
              {importJob.status}
            </Tag>
            {importJob.sourceFormat ? (
              <Tag>{importJob.sourceFormat}</Tag>
            ) : null}
          </p>
        )}
        {importJob?.originalFilename && (
          <p>
            {t('cellLines.importFile')}: {importJob.originalFilename}
          </p>
        )}
        {importJob && (
          <>
            <p>
              {t('cellLines.totalRows')}: {importJob.totalRows}
            </p>
            <p>
              {t('cellLines.validRows')}: {importJob.validRows}
            </p>
            <p>
              {t('cellLines.errorRows')}: {importJob.errorRows}
            </p>
          </>
        )}
        {importJob?.executedAt && (
          <p>
            {t('cellLines.executedAt')}:{' '}
            {formatTenantDateTime(importJob.executedAt)}
          </p>
        )}
        {importJob && importJob.errorRows > 0 && (
          <Button
            icon={<DownloadOutlined />}
            href={`/api/v1/imports/${importJob.id}/errors.csv`}
            style={{ marginBottom: 12 }}
          >
            {t('cellLines.downloadErrors')}
          </Button>
        )}
        {importJob && importJob.errors.length > 0 && (
          <Table
            size="small"
            rowKey={(row) => `${row.rowNumber}-${row.code}-${row.field ?? ''}`}
            dataSource={importJob.errors}
            pagination={{ defaultPageSize: 5 }}
            columns={[
              { title: t('cellLines.rowNumber'), dataIndex: 'rowNumber' },
              {
                title: t('cellLines.field'),
                dataIndex: 'field',
                render: (value: string | null) => value ?? '-',
              },
              { title: t('cellLines.errorCode'), dataIndex: 'code' },
              { title: t('cellLines.messageKey'), dataIndex: 'messageKey' },
            ]}
          />
        )}
      </Modal>
    </section>
  );
}
